import {AfterViewInit, Component, OnDestroy, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {Partner} from '../../../shared/interface/partner';
import {PartnerService} from '../../../shared/services/partner.service';
import {ReplaySubject} from 'rxjs';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {NgForm} from '@angular/forms';
import {delay, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {Uuid} from '../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {FilterService} from '../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';
import {SubmissionsComponent} from "../../submissions/submissions.component";
import {ReportCustomComponent} from "../../report-custom/report-custom.component";
import {REPORT_TYPE_PARTNER, REPORT_TYPE_PARTNER_REGIONAL} from "../../../shared/services/dict.service";
import {Sort} from "@angular/material/sort";

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class ListComponent implements AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;

  initialFilters: any;
  displayedColumns: string[] = ['name', 'status', 'email', 'phone', 'assetsRegional', 'nip', 'regon', 'headquartersAddress', 'actions', ];
  dataSource: Partner[] = [];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'shortName';
  sortOrder = 'ASC';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  public constructor(
    private partnerService: PartnerService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.partnerService.getPartnerListSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: CountedAndPaginated<Partner>) => {
        this.dataSource = result.items;
        this.totalData = result.pagination.total;
      });
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      recordId: 'string',
      legalForm: 'int',
      in$formOfBusiness: 'int[]',
      contains$fullName: 'string',
      contains$shortName: 'string',
      contains$nip: 'string',
      contains$regon: 'string',
      contains$krs: 'string',
      email: 'string',
      phone: 'string',
      contains$accountNumber: 'string',
      vatPayer: 'int',
      in$status: 'int[]',
      assetsRegional: 'string[]',
      lifeRegional: 'string[]',
      address: 'string',
      state: 'int',
      tags: 'int[]',
      verified: 'int',
      selfBilling: 'int',
      headers: 'array',
      activeSelfBillingAsf: 'int',
      activeSelfBillingPremium: 'int',
      newPartner: 'int',
      gte$createdAt: 'date',
      lte$createdAt: 'date',
      ASF360Access: 'int',
      verificationSalesProcess: 'int',
      productMessageConsent: 'int',
      contestMessageConsent: 'int',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
        this.router.navigate(['partnerzy', this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          ...this.filtersForm.value
        })]);
      });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.sortBy = state.params?.sortBy ? state.params.sortBy : 'shortName';
        this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'ASC';
        this.loadListData();
      })
    ;
  }

  filter(): void {
    this.router.navigate(['partnerzy', this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      ...this.filtersForm.value,
    })]);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.partnerService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters,
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      }
      );
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate(['partnerzy', {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  downloadReport(): void {
    this.partnerService.downloadReport(this.filtersForm.value)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  downloadReportAffiliateCode(): void {
    this.partnerService.downloadReportAffiliateCode(this.filtersForm.value)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  buildFilters(headers = false): any {
    const filters = {...this.initialFilters};

    if (headers){
      filters.headers = 'Pełna nazwa,Skrócona nazwa,NIP,REGON,Ulica siedziby,Nr budynku siedziby,' +
        'Nr mieszkania siedziby,Kod pocztowy siedziby,Województwo siedziby,Miejscowość siedziby,' +
        'Powiat siedziby,Email,Email rozliczeniowy,Email marketingowy,Telefon,Status,Nazwa Partnera do analiz,' +
        'Nowy Partner,Partner Nadprowizja,Wiadomości produktowe';
    }

    return filters;
  }

  downloadReportRegional(): void {
    this.partnerService.downloadCustomReportRegional(this.buildFilters(true))
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  deletePartner(partner: Partner): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć partnera?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.partnerService.deletePartner(partner.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto partnera', '', {duration: 2000});
            this.loadListData();
          }, (error: HttpErrorResponse) => {
            if (error.status === 400) {
              this.snack.open(error.error.error.text, '400', {duration: 4000});
            }
          });
      }
    });
  }
  openReportDialog(): void {
    const dialogRef = this.dialog.open(ReportCustomComponent, {
      width: '70%',
      data: {filter: this.filtersForm,
        reportName: REPORT_TYPE_PARTNER},
    });
  }

  openReportDialogRegional(): void {
    const dialogRef = this.dialog.open(ReportCustomComponent, {
      width: '70%',
      data: {filter: this.filtersForm,
        reportName: REPORT_TYPE_PARTNER_REGIONAL},
    });
  }

  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;
    this.loadListData();
  }
}
